import React, { useEffect, useState } from 'react';
import {
  TextField, Button, Box, Typography, Alert, Stack, Avatar, Divider, CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AppCard from '../common/AppCard';
import AppDialog from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LockResetIcon from '@mui/icons-material/LockReset';
import DevicesIcon from '@mui/icons-material/Devices';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const actionBtnSx = { borderRadius: 3, textTransform: 'none', fontWeight: 600, boxShadow: 'none', px: 2.5 };

export default function AccountSecurityCard({ notify }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);

  // Change password dialog
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  // Log out everywhere
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);

  // Delete account dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get('/auth/me')
      .then(res => setProfile(res.data.user))
      .catch(() => setProfile(null));
  }, []);

  const socialLogin = Boolean(profile?.providers?.length);

  const closePwDialog = () => {
    setPwOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwError('');
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (newPassword.length < 8) {
      setPwError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError("New passwords don't match.");
      return;
    }
    setPwSaving(true);
    try {
      const res = await api.post('/auth/change-password', { currentPassword, newPassword });
      // Other sessions are now logged out; keep this one alive with the fresh token
      if (res.data?.token) localStorage.setItem('token', res.data.token);
      closePwDialog();
      notify('Password changed. Other devices have been logged out.');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setPwSaving(false);
    }
  };

  const handleLogoutAll = async () => {
    setLoggingOutAll(true);
    try {
      await api.post('/auth/logout-all');
      logout();
      navigate('/login');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to log out everywhere.', 'error');
      setLoggingOutAll(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setDeleteEmail('');
    setDeletePassword('');
    setDeleteError('');
  };

  const emailMatches = profile?.email &&
    deleteEmail.trim().toLowerCase() === profile.email.toLowerCase();

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await api.delete('/auth/account', {
        data: socialLogin ? {} : { password: deletePassword }
      });
      logout();
      navigate('/login');
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete account.');
      setDeleting(false);
    }
  };

  return (
    <AppCard title="Account & Security" icon={<ShieldOutlinedIcon />}>
      {profile === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* Signed-in identity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={profile.profilePicture || undefined}
              sx={{
                width: 44, height: 44,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                fontWeight: 700
              }}
            >
              {(profile.name || profile.email || '?')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {profile.name || profile.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {profile.name ? profile.email : (
                  profile.createdAt
                    ? `Member since ${new Date(profile.createdAt).toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}`
                    : ''
                )}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            {!socialLogin && (
              <Button
                variant="outlined"
                startIcon={<LockResetIcon />}
                onClick={() => setPwOpen(true)}
                sx={actionBtnSx}
              >
                Change password
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<DevicesIcon />}
              onClick={() => setLogoutAllOpen(true)}
              sx={actionBtnSx}
            >
              Log out everywhere
            </Button>
          </Stack>

          <Divider />

          {/* Danger zone */}
          <Box>
            <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
              Danger zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Permanently delete your account, all of your data, and revoke bank access.
              This cannot be undone.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setDeleteOpen(true)}
              sx={actionBtnSx}
            >
              Delete account
            </Button>
          </Box>
        </Stack>
      )}

      {/* Change password */}
      <AppDialog
        open={pwOpen}
        onClose={closePwDialog}
        title="Change password"
        primaryLabel={pwSaving ? 'Saving…' : 'Change password'}
        onPrimary={handleChangePassword}
        primaryDisabled={pwSaving || !currentPassword || !newPassword || !confirmPassword}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {pwError && <Alert severity="error">{pwError}</Alert>}
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            fullWidth
            size="small"
            autoComplete="current-password"
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            fullWidth
            size="small"
            autoComplete="new-password"
            helperText="At least 8 characters"
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            fullWidth
            size="small"
            autoComplete="new-password"
          />
          <Typography variant="caption" color="text.secondary">
            Changing your password logs you out of all other devices.
          </Typography>
        </Stack>
      </AppDialog>

      {/* Log out everywhere */}
      <ConfirmDialog
        open={logoutAllOpen}
        onClose={() => setLogoutAllOpen(false)}
        title="Log out everywhere?"
        message="This signs you out on every device, including this one. You'll need to log in again."
        confirmLabel="Log out everywhere"
        confirmColor="primary"
        onConfirm={handleLogoutAll}
        loading={loggingOutAll}
      />

      {/* Delete account */}
      <AppDialog
        open={deleteOpen}
        onClose={closeDeleteDialog}
        title="Delete your account?"
        primaryLabel={deleting ? 'Deleting…' : 'Delete my account'}
        primaryColor="error"
        onPrimary={handleDeleteAccount}
        primaryDisabled={deleting || !emailMatches || (!socialLogin && !deletePassword)}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="error">
            This permanently deletes your account, every transaction, budget, rule, category
            and account, and revokes Budget Clues' access to your bank. It cannot be undone.
          </Alert>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
          <TextField
            label={`Type your email (${profile?.email || ''}) to confirm`}
            value={deleteEmail}
            onChange={e => setDeleteEmail(e.target.value)}
            fullWidth
            size="small"
            autoComplete="off"
          />
          {!socialLogin && (
            <TextField
              label="Your password"
              type="password"
              value={deletePassword}
              onChange={e => setDeletePassword(e.target.value)}
              fullWidth
              size="small"
              autoComplete="current-password"
            />
          )}
        </Stack>
      </AppDialog>
    </AppCard>
  );
}
